import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, TextField, CircularProgress, FormControl, InputLabel, Select, MenuItem, Avatar, Tooltip, Grid } from '@mui/material';
import ProfilePictureUpload from 'components/contacts/contactDetails/editContactDetails/ProfilePictureUpload';
import PhoneInputField from 'components/common/PhoneInputField';
import { updateLead, updateLeadProfilePicture } from 'apis/leadCaptureApi';
import { uploadImageBlob, deleteImage } from 'apis/imageStorageApi';
import { getCardById } from 'apis/cardApi';
import { getUserById } from 'apis/authApi';
import avatarPlaceholder from 'images/avatar-cropped.png';

// Styles for text fields
const textFieldStyles = {
    backgroundColor: '#f7f7f7',
    borderRadius: '12px',
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '& fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '&:hover fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
            borderRadius: '12px',
        },
        '& input': {
            borderRadius: '12px',
            padding: '10px 12px',
        },
        '& input::placeholder': {
            fontSize: '0.8rem',
            fontWeight: '300',
        },
    },
};

// Define lead fields - matching detail page order
const LEAD_FIELDS = [
    { label: 'Name', name: 'leadName', required: true },
    { label: 'Email', name: 'leadEmail', required: true },
    { label: 'Job Title', name: 'leadJobTitle' },
    { label: 'Company', name: 'leadCompany' },
    { label: 'CRM Link', name: 'crmLink' },
    { label: 'Website', name: 'website' },
    { label: 'Location', name: 'location' },
    { label: 'Instagram', name: 'instagram' },
    { label: 'LinkedIn', name: 'linkedin' },
    { label: 'Card Source', name: 'cardSource' }
];

const TeamLeadDetailsEdit = ({ lead, onUpdate, onCancel }) => {
    const [formData, setFormData] = useState({
        leadName: lead?.leadName || '',
        leadEmail: lead?.leadEmail || '',
        leadJobTitle: lead?.leadJobTitle || '',
        leadCompany: lead?.leadCompany || '',
        crmLink: lead?.crmLink || '',
        exportedTo: lead?.exportedTo || '',
        leadPhoneNumber: lead?.leadPhoneNumber || '',
        website: lead?.website || '',
        linkedin: lead?.linkedin || '',
        instagram: lead?.instagram || '',
        location: lead?.location || '',
        cardSource: lead?.cardSource || '',
        bio: lead?.bio || '',
        notes: lead?.notes || '',
        department: lead?.department || '',
        role: lead?.role || '',
        profilePicture: null,
        profilePictureUrl: lead?.profilePictureUrl || null
    });
    const [loading, setLoading] = useState(false);
    const [phoneError, setPhoneError] = useState('');
    const [connectedMember, setConnectedMember] = useState(null);

    // Update form data when lead changes
    useEffect(() => {
        setFormData({
            leadName: lead?.leadName || '',
            leadEmail: lead?.leadEmail || '',
            leadJobTitle: lead?.leadJobTitle || '',
            leadCompany: lead?.leadCompany || '',
            crmLink: lead?.crmLink || '',
            exportedTo: lead?.exportedTo || '',
            leadPhoneNumber: lead?.leadPhoneNumber || '',
            website: lead?.website || '',
            linkedin: lead?.linkedin || '',
            instagram: lead?.instagram || '',
            location: lead?.location || '',
            cardSource: lead?.cardSource || '',
            bio: lead?.bio || '',
            notes: lead?.notes || '',
            department: lead?.department || '',
            role: lead?.role || '',
            profilePicture: null,
            profilePictureUrl: lead?.profilePictureUrl || null
        });
    }, [lead]);

    // Fetch the team member who owns the card this lead is assigned to
    useEffect(() => {
        const fetchTeamMember = async () => {
            try {
                // First try to get the card owner from cardId (for reassigned leads)
                if (lead?.cardId) {
                    console.log('🔍 Fetching card owner info for cardId:', lead.cardId);
                    const cardResponse = await getCardById(lead.cardId);

                    if (cardResponse && cardResponse.card) {
                        const card = cardResponse.card;

                        // Match leads table behavior: connected image comes from the card first.
                        setConnectedMember({
                            name: card.cardName || card.contact_info?.fullName || 'Card',
                            avatar: card.profilePictureUrl || null,
                            connectedDate: lead.createdAt || lead.connectedDate
                        });

                        // Enrich with owner user info when available, but keep card avatar priority.
                        if (card.userId) {
                            const cardOwnerUserId = card.userId;
                            console.log('🔍 Fetching team member info for card owner userId:', cardOwnerUserId);
                            const userData = await getUserById(cardOwnerUserId);

                            if (userData && (userData.user || userData.data)) {
                                const user = userData.user || userData.data;
                                setConnectedMember({
                                    name: user.fullName || user.email || card.cardName || card.contact_info?.fullName || 'Card',
                                    avatar: card.profilePictureUrl || user.profilePictureUrl || null,
                                    connectedDate: lead.createdAt || lead.connectedDate
                                });
                            }
                        }
                        return;
                    }
                }
                
                // Fallback: Use userId if cardId is not available (for older leads)
                if (lead?.userId) {
                    console.log('🔍 Fallback: Fetching team member info for userId:', lead.userId);
                    const userData = await getUserById(lead.userId);

                    if (userData && (userData.user || userData.data)) {
                        const user = userData.user || userData.data;
                        setConnectedMember({
                            name: user.fullName || user.email,
                            avatar: user.profilePictureUrl || null,
                            connectedDate: lead.createdAt || lead.connectedDate
                        });
                        console.log('✅ Team member info fetched:', user.fullName || user.email);
                        return;
                    }
                }
            } catch (error) {
                console.error('Error fetching team member info:', error);
                // Fallback - show basic info if fetch fails
                setConnectedMember({
                    name: 'Team Member',
                    avatar: null,
                    connectedDate: lead.createdAt || lead.connectedDate
                });
            }
        };

        if (lead) {
            fetchTeamMember();
        }
    }, [lead]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle phone number changes
    const handlePhoneChange = (value) => {
        setFormData(prev => ({
            ...prev,
            leadPhoneNumber: value
        }));
        setPhoneError('');
    };

    // Update profile picture
    const handleProfilePictureUpdate = (pictureBlob) => {
        setFormData(prev => ({
            ...prev,
            profilePicture: pictureBlob
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const requiredFields = ['leadName', 'leadEmail'];
        const isValid = requiredFields.every(field => formData[field]?.trim());

        if (!isValid) {
            setLoading(false);
            alert('Please fill in required fields (Name and Email)');
            return;
        }

        try {
            let profilePictureUrl = formData.profilePictureUrl;

            // Handle profile picture upload if new picture is provided
            if (formData.profilePicture) {
                try {
                    // Delete old profile picture if it exists
                    if (lead?.profilePictureUrl) {
                        await deleteImage('lead', 'profile', lead._id);
                    }

                    // Upload new profile picture
                    const uploadedUrl = await uploadImageBlob(
                        formData.profilePicture,
                        null,
                        'lead',
                        'profile',
                        lead._id
                    );
                    profilePictureUrl = uploadedUrl;

                    // Update profile picture URL in database
                    await updateLeadProfilePicture(lead._id, profilePictureUrl);
                } catch (uploadError) {
                    console.error('Profile picture upload error:', uploadError);
                    // Continue with other updates even if profile picture fails
                }
            }

            // Prepare updated lead data
            const updatedLeadData = {
                leadName: formData.leadName.trim(),
                leadEmail: formData.leadEmail.trim(),
                leadJobTitle: formData.leadJobTitle.trim(),
                leadCompany: formData.leadCompany.trim(),
                leadPhoneNumber: formData.leadPhoneNumber,
                website: formData.website.trim(),
                leadAddress: formData.location.trim(),
                instagram: formData.instagram.trim(),
                linkedin: formData.linkedin.trim(),
                leadNotes: formData.notes.trim(),
                crmLink: formData.crmLink.trim(),
                cardSource: formData.cardSource.trim(),
                exportedTo: formData.exportedTo,
                profilePictureUrl: profilePictureUrl
            };

            // Call API to update lead
            const response = await updateLead(lead._id, updatedLeadData);

            if (response && response.success) {
                // Call onUpdate callback with updated data
                if (onUpdate) {
                    onUpdate({
                        ...lead,
                        ...updatedLeadData,
                        _id: lead._id
                    });
                }
            } else {
                throw new Error(response?.message || 'Failed to update lead');
            }
        } catch (error) {
            console.error('Error updating lead:', error);
            alert(error.message || 'Failed to update lead. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ mt: 3, overflow: 'hidden' }}>
            <form onSubmit={handleSubmit}>
                {/* Profile Picture Upload */}
                <ProfilePictureUpload
                    initialProfilePicture={lead?.profilePictureUrl}
                    onProfilePictureUpdate={handleProfilePictureUpdate}
                />

                {/* Lead Fields */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    {LEAD_FIELDS.map((field) => (
                        <Box key={field.name}>
                            <Typography
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: '#4f4f4f',
                                    marginBottom: '10px'
                                }}
                            >
                                {field.label}
                                {field.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                            </Typography>
                            <TextField
                                fullWidth
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={handleInputChange}
                                placeholder={field.label}
                                required={field.required}
                                sx={{
                                    ...textFieldStyles,
                                    '& .MuiInputBase-root': {
                                        height: '48px'
                                    }
                                }}
                            />
                        </Box>
                    ))}

                    {/* Phone Number Field */}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Phone Number
                        </Typography>
                        <PhoneInputField
                            value={formData.leadPhoneNumber}
                            onChange={handlePhoneChange}
                            error={phoneError}
                            placeholder="Enter phone number"
                            containerStyle={{ marginBottom: '10px' }}
                        />
                    </Box>

                    {/* Exported to Field */}
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Exported to
                        </Typography>
                        <FormControl
                            fullWidth
                            sx={{
                                ...textFieldStyles,
                                '& .MuiInputBase-root': {
                                    height: '48px'
                                }
                            }}
                        >
                            <Select
                                value={formData.exportedTo || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, exportedTo: e.target.value }))}
                                displayEmpty
                                sx={{
                                    backgroundColor: '#f7f7f7',
                                    borderRadius: '12px',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        border: 'none',
                                    },
                                }}
                            >
                                <MenuItem value="">Select CRM</MenuItem>
                                <MenuItem value="Salesforce">Salesforce</MenuItem>
                                <MenuItem value="HubSpot">HubSpot</MenuItem>
                                <MenuItem value="Pipedrive">Pipedrive</MenuItem>
                                <MenuItem value="None">None</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Bio Field (Full Width) */}
                    <Box sx={{ gridColumn: '1 / span 2', marginTop: 2 }}>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Bio
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            name="bio"
                            value={formData.bio || ''}
                            onChange={handleInputChange}
                            placeholder="Bio"
                            sx={{
                                ...textFieldStyles,
                                '& .MuiInputBase-root': {
                                    height: 'auto',
                                    minHeight: '90px'
                                }
                            }}
                        />
                    </Box>

                    {/* Notes Field (Full Width) */}
                    <Box sx={{ gridColumn: '1 / span 2', marginTop: 2 }}>
                        <Typography
                            sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                marginBottom: '10px'
                            }}
                        >
                            Notes
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            name="notes"
                            value={formData.notes || ''}
                            onChange={handleInputChange}
                            placeholder="Notes"
                            sx={{
                                ...textFieldStyles,
                                '& .MuiInputBase-root': {
                                    height: 'auto',
                                    minHeight: '90px'
                                }
                            }}
                        />
                    </Box>

                    {/* Connected With Field (Full Width) - Display only, not editable */}
                    {connectedMember && (
                        <Box sx={{ gridColumn: '1 / span 2', marginTop: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: '#4f4f4f',
                                    marginBottom: '10px'
                                }}
                            >
                                Connected with
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: '10px 12px', backgroundColor: '#f7f7f7', borderRadius: '12px' }}>
                                <Avatar
                                    src={connectedMember.avatar || avatarPlaceholder}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: '#e0e0e0'
                                    }}
                                >
                                    {!connectedMember.avatar && (() => {
                                        const name = connectedMember.name || '';
                                        const nameParts = name.split(' ');
                                        return nameParts
                                            .map(part => part.charAt(0).toUpperCase())
                                            .slice(0, 2)
                                            .join('');
                                    })()}
                                </Avatar>
                                <Typography
                                    sx={{
                                        color: '#242020',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                    }}
                                >
                                    {connectedMember.name}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Action Buttons */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 2,
                        marginTop: 3,
                        paddingTop: 2,
                        borderTop: '1px solid #e0e0e0'
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        disabled={loading}
                        sx={{
                            backgroundColor: 'white',
                            color: '#4f4f4f',
                            borderRadius: '20px',
                            border: '1px solid #e3e3e3',
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            '&:hover': {
                                backgroundColor: 'white',
                                boxShadow: 2,
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        sx={{
                            backgroundColor: '#000',
                            color: '#fff',
                            borderRadius: '20px',
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            '&:hover': {
                                backgroundColor: '#000',
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Update'}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default TeamLeadDetailsEdit;